import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { InfoIcon } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ResponsiveActionLinks, useOptionsWithDeleted, usePermissions } from '@/modules/common';
import { DatePicker } from '@/modules/common/components/molecules/date/date-picker';
import { PhoneNumberInput } from '@/modules/common/components/molecules/inputs/phone-number-input';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { SingleSelect } from '@/modules/common/components/molecules/selectors/single-select/single-select';
import { SingleSelectWithPagination } from '@/modules/common/components/molecules/selectors/single-select/single-select-with-pagination';
import { ToggleWithDescription } from '@/modules/common/components/molecules/toggle-with-description';
import { FileUploadField } from '@/modules/common/components/organisms/file-upload-field';
import {
  BaseFeatureTab,
  BaseTabProps,
  FeatureMode,
  FeatureTabRef,
  SubmitDateRange,
  useTabFormState,
} from '@/modules/common/components/templates/feature-details';
import {
  buildInitialCustomAttributesFormData,
  mapCustomAttributeDTOToFormData,
  mapCustomAttributesToDTO,
} from '@/modules/common/mappers/custom-attribute.mapper';
import { CustomAttributesFormData } from '@/modules/common/types/custom-attribute';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { ResolveConflictsModal } from '@/modules/projects/components/modals/resolve-conflicts-modal';
import { CustomAttributesFields } from '@/modules/projects/components/project-details/info/custom-attributes-fields';
import { useDepartments, useRoles, useSeniorities } from '@/modules/settings/hooks';
import { useTeamMemberSelectors } from '@/modules/team';
import { MembershipTypeSelect } from '@/modules/team/components/membership-type-select';
import { useCheckTeamMemberInfoConflicts } from '@/modules/team/hooks/info/use-check-team-member-info-conflicts';
import { useCustomAttributes } from '@/modules/team/hooks/use-custom-attributes';
import { useTeamMemberChangeConflictResolution } from '@/modules/team/hooks/use-team-member-change-conflict-resolution';
import {
  teamMemberInfoConflictFields,
  TeamMemberInfoFieldKey,
} from '@/modules/team/utils/change-conflicts/team-member-info-conflict-config';
import { SettingsRoutes } from '@/router/routes';
import { compareFormValues } from '@/utils/form-state.utils';
import { logger } from '@/utils/logger/logger';

import {
  getCountriesData,
  loadCities,
  loadCountries,
  searchCities,
  searchCountries,
} from '../../../api/location.api';
import { MemberStatus, TeamMember, TeamMemberFormData, TeamMemberType } from '../../../types';
import { MembershipType } from '../../../types/membership-type';
import { isValidMembershipType } from '../../../utils/membership-type.utils';
import {
  TeamMemberInfoFormData,
  teamMemberInfoSchema,
} from '../../../validations/team-member-info-schema';

// Create compatible interfaces that use the broader TeamMemberFormData type
// but work with the info tab's specific form data
export interface TeamMemberInfoTabProps extends BaseTabProps<TeamMember, TeamMemberFormData> {
  /**
   * Callback to directly save data to API after conflict resolution
   * This bypasses the normal form submission flow and conflict detection
   */
  onDirectSave?: (
    data: TeamMemberFormData,
    effectiveStartDate?: Date,
    effectiveEndDate?: Date | null
  ) => Promise<void>;
}

export interface TeamMemberInfoTabRef extends FeatureTabRef<TeamMemberFormData> {}

const TAG = 'TeamMemberInfoTab:';

/**
 * Team member information form component supporting create, edit, and read modes.
 * Handles team member details with draft persistence, validation, and comprehensive field management.
 *
 * Features:
 * - Multi-mode operation with role-based field visibility
 * - Draft data persistence for create mode
 * - Dynamic form fields based on selection states
 * - Location fields with country/city dependencies
 * - Real-time validation and dirty state detection
 */
export const TeamMemberInfoTab = forwardRef<TeamMemberInfoTabRef, TeamMemberInfoTabProps>(
  (props, ref) => {
    const { mode, featureData: teamMemberData, draftData, onDirectSave, onFormStateChange } = props;
    const { t } = useTranslation(['authenticated.team', 'common']);

    // State to control when to fetch each dependency independently
    const [shouldFetchDepartments, setShouldFetchDepartments] = useState(false);
    const [shouldFetchRoles, setShouldFetchRoles] = useState(false);
    const [shouldFetchSeniorities, setShouldFetchSeniorities] = useState(false);
    // Load dependencies separately with lazy loading
    const { data: departments, isLoading: isLoadingDepartments } =
      useDepartments(shouldFetchDepartments);
    const { data: roles, isLoading: isLoadingRoles } = useRoles(shouldFetchRoles);
    const { data: seniorities, isLoading: isLoadingSeniorities } =
      useSeniorities(shouldFetchSeniorities);
    const { loadManagers, searchManagers } = useTeamMemberSelectors();
    const { checkConflicts, isPending: isCheckingConflicts } = useCheckTeamMemberInfoConflicts();

    // Custom attributes are rendered as the last section of the Info tab. The list of
    // available attributes (already filtered to supported select types) drives both the
    // rendered fields and the form-data → DTO mapping on save.
    const { data: customAttributes } = useCustomAttributes();

    // Use hook to include deleted entities in options if currently selected
    const departmentOptions = useOptionsWithDeleted(teamMemberData?.department, departments || []);
    const rolesOptions = useOptionsWithDeleted(teamMemberData?.role, roles || []);
    const senioritiesOptions = useOptionsWithDeleted(teamMemberData?.seniority, seniorities || []);

    const navigate = useNavigate();
    const { hasPermission } = usePermissions();

    const [selectedCountry, setSelectedCountry] = useState<string>('');
    // Resolved country code for city loading — separate from the form field so it doesn't affect display
    const [resolvedCountryCode, setResolvedCountryCode] = useState<string>('');
    const [isResolvingCountryCode, setIsResolvingCountryCode] = useState(false);
    // Tracks whether countryName was ever non-empty, so we can detect a user-initiated clear
    // vs. initial render where both country fields start empty (Create mode)
    const countryNameWasSetRef = useRef(false);
    const [selectedManagerName, setSelectedManagerName] = useState<string | undefined>(
      teamMemberData?.manager?.name
    );

    // Sync manager name when teamMemberData loads asynchronously
    useEffect(() => {
      if (teamMemberData?.manager?.name && !selectedManagerName) {
        setSelectedManagerName(teamMemberData.manager.name);
      }
    }, [teamMemberData?.manager?.name]);

    /**
     * Builds initial form values based on mode and available data sources.
     * Priority: draftData (create) > teamMemberData (edit/read) > defaults (create)
     */
    const initialValues = useMemo((): TeamMemberInfoFormData => {
      if (mode === FeatureMode.Create && draftData) {
        return {
          name: draftData.name || '',
          email: draftData.email || '',
          phoneNumber: draftData.phoneNumber || '',
          isPlaceholder: Boolean('isPlaceholder' in draftData ? draftData.isPlaceholder : false),
          country: '', // Will be set by SingleSelectWithPagination when country is selected
          countryName: draftData.country || '', // For Create mode with draftData, country contains the name
          city: '', // Will be set by SingleSelectWithPagination when city is selected
          cityName: draftData.city || '', // For Create mode with draftData, city contains the name
          tags: draftData.tags || [],
          membershipType: draftData.membershipType ?? MembershipType.InHouse,
          about: draftData.about || '',
          department:
            typeof draftData.department === 'object'
              ? draftData.department?.id?.toString()
              : draftData.department?.toString() || '',
          role:
            typeof draftData.role === 'object'
              ? draftData.role?.id?.toString()
              : draftData.role?.toString() || '',
          seniority:
            typeof draftData.seniority === 'object'
              ? draftData.seniority?.id?.toString()
              : draftData.seniority?.toString() || '',
          isManager: draftData.isManager || false,
          managerId: draftData.managerId?.toString() || '',
          dateOfJoin: draftData.dateOfJoin,
          // Restore custom attributes from the draft (persisted in DTO shape) so
          // selections survive tab switches during creation. Seed all available
          // attribute keys so the form value matches RHF's registered fields.
          customAttributes: draftData.customAttributesValues
            ? {
                ...buildInitialCustomAttributesFormData(customAttributes ?? [], []),
                ...mapCustomAttributeDTOToFormData(
                  draftData.customAttributesValues,
                  customAttributes ?? []
                ),
              }
            : buildInitialCustomAttributesFormData(customAttributes ?? [], []),
        };
      }

      if (mode === FeatureMode.Create) {
        return {
          name: '',
          email: '',
          phoneNumber: '',
          isPlaceholder: false,
          country: '',
          countryName: '',
          city: '',
          cityName: '',
          tags: [],
          membershipType: MembershipType.InHouse,
          about: '',
          department: '',
          role: '',
          seniority: '',
          isManager: false,
          managerId: '',
          dateOfJoin: undefined,
          customAttributes: buildInitialCustomAttributesFormData(customAttributes ?? [], []),
        };
      }

      // For edit/read mode
      return {
        name: teamMemberData?.name || '',
        email: teamMemberData?.email || '',
        phoneNumber: teamMemberData?.phoneNumber || '',
        isPlaceholder: teamMemberData?.type === TeamMemberType.Placeholder || false,
        country: '', // Will be set by SingleSelectWithPagination when country data loads
        countryName: teamMemberData?.country || '', // In edit mode, country contains the name from backend
        city: '', // Will be set by SingleSelectWithPagination when city data loads
        cityName: teamMemberData?.city || '', // In edit mode, city contains the name from backend
        tags: teamMemberData?.tags || [],
        membershipType: teamMemberData?.membershipType ?? MembershipType.InHouse,
        about: teamMemberData?.about || '',
        department:
          typeof teamMemberData?.department === 'object' && teamMemberData.department !== null
            ? teamMemberData.department.id.toString()
            : '',
        role:
          typeof teamMemberData?.role === 'object' && teamMemberData.role !== null
            ? teamMemberData.role.id.toString()
            : '',
        seniority:
          typeof teamMemberData?.seniority === 'object' && teamMemberData.seniority !== null
            ? teamMemberData.seniority.id.toString()
            : '',
        isManager: teamMemberData?.isManager || false,
        managerId: teamMemberData?.manager?.id?.toString() || '',
        dateOfJoin: teamMemberData?.dateOfJoin ?? undefined,
        // Seed every available attribute key (not just those with values) so the
        // initial form value matches RHF's registered fields — otherwise the form
        // would appear dirty on open. See buildInitialCustomAttributesFormData.
        customAttributes: buildInitialCustomAttributesFormData(
          customAttributes ?? [],
          teamMemberData?.customAttributes ?? []
        ),
      };
    }, [mode, teamMemberData, draftData, customAttributes]);

    const form = useForm<TeamMemberInfoFormData>({
      resolver: zodResolver(teamMemberInfoSchema(t)),
      mode: 'onTouched',
      defaultValues: initialValues,
    });

    // Reset form when initialValues change (important for edit mode)
    useEffect(() => {
      form.reset(initialValues);
    }, [form, initialValues]);

    // Watch form validity state to ensure it's tracked reactively
    // This is needed for the customDirtyStateFn to properly react to validation changes
    const isFormValid = form.formState.isValid;

    // Create adapter function to convert TeamMemberInfoFormData to TeamMemberFormData
    const getFormDataAdapter = (): TeamMemberFormData => {
      const infoData = form.getValues();
      // When placeholder is toggled on, fields hidden in the UI (email, phoneNumber,
      // country, city, about) must be stripped from the payload — otherwise stale values
      // left in form state (e.g. an invalid email the user typed before toggling) would
      // be sent to the backend. Avatar is intentionally kept: the upload field stays
      // visible in Create mode for placeholders and a selected image must be submitted.
      const isPlaceholderMember = !!infoData.isPlaceholder;
      // Only return Info tab specific fields - don't include fields from other tabs
      // This prevents overwriting data from Billing/Capacity/Integrations tabs in Create mode
      return {
        name: infoData.name,
        email: isPlaceholderMember ? '' : infoData.email,
        phoneNumber: isPlaceholderMember ? '' : infoData.phoneNumber,
        country: isPlaceholderMember ? '' : infoData.countryName || '', // Submit country name, not country code
        city: isPlaceholderMember ? '' : infoData.cityName || '', // Submit city name, not geonameId
        tags: infoData.tags || [],
        // Backend rejects a null membershipType (OPS-1472: 500 "Argument membershipType
        // must not be null"). The field is clearable in the UI (clearing sets '' which
        // is neither a valid enum value nor caught by `?? null`), so always coerce an
        // empty/invalid value to the default rather than sending null.
        membershipType: isValidMembershipType(infoData.membershipType)
          ? infoData.membershipType
          : MembershipType.InHouse,
        about: isPlaceholderMember ? '' : infoData.about,
        isManager: infoData.isManager || false,
        status: teamMemberData?.status || MemberStatus.Active,
        type: isPlaceholderMember ? TeamMemberType.Placeholder : TeamMemberType.TeamMember,
        role: infoData.role || undefined,
        department: infoData.department || undefined,
        seniority: infoData.seniority || undefined,
        managerId: infoData.managerId ?? teamMemberData?.manager?.id?.toString(),
        manager: teamMemberData?.manager,
        avatarLink: infoData.avatar, // Form field is 'avatar', TeamMemberFormData expects 'avatarLink'
        isPlaceholder: isPlaceholderMember,
        // Custom attribute values, mapped to the DTO shape and sent through the same
        // info endpoint. Keyed by the list of available (supported select) attributes.
        customAttributesValues: customAttributes
          ? mapCustomAttributesToDTO(
              (infoData.customAttributes ?? {}) as CustomAttributesFormData,
              customAttributes
            )
          : undefined,
      } as TeamMemberFormData;
    };

    // Non-versioned fields that don't require date range when changed.
    const NON_VERSIONED_FIELDS: (keyof TeamMemberInfoFormData)[] = [
      'name',
      'email',
      'phoneNumber',
      'isPlaceholder',
      'about',
      'tags',
      'avatar',
      'customAttributes',
    ];

    // Use the tab form state hook with adapter
    const { isReadMode, handleSubmit: baseHandleSubmit } = useTabFormState<
      TeamMember,
      TeamMemberInfoFormData
    >(props as BaseTabProps<TeamMember, TeamMemberInfoFormData>, {
      form,
      initialValues,
      isArchivedFn: member => member?.status === MemberStatus.Archived,
      triggerValidationOnMount: true,
      nonVersionedFields: NON_VERSIONED_FIELDS,
      customDirtyStateFn: (currentValues, initialValues) => {
        const hasRequiredName = (currentValues.name?.trim()?.length ?? 0) > 0;

        if (mode === FeatureMode.Create) {
          // Create mode: Check if form is valid and has required data
          // Email is optional for all team member types
          return Boolean(hasRequiredName && isFormValid);
        } else {
          // Edit mode: Check if there are changes from initial values
          // Note: validity is tracked separately and controls the save button / tab error icon
          if (initialValues) {
            // Ignore country and city fields in dirty check as they are internally managed
            return compareFormValues(
              { ...currentValues, country: '', city: '' },
              { ...initialValues, country: '', city: '' }
            );
          }

          // Fallback to form's built-in dirty state
          return form.formState.isDirty;
        }
      },
    });

    /**
     * Creates form values with entity objects ({ id, name }) for conflict
     * checking and the conflict resolution modal dropdown labels.
     * Falls back to { id, name: '' } when options haven't loaded yet.
     */
    const getFormValuesForConflicts = (): Record<TeamMemberInfoFieldKey, unknown> => {
      const formValues = form.getValues();

      const findEntity = (
        id: string | null | undefined,
        options: { id: number; name: string }[]
      ): { id: number; name: string } | null => {
        if (!id) return null;
        return options.find(o => o.id.toString() === id) ?? { id: Number(id), name: '' };
      };

      return {
        status: teamMemberData?.status || MemberStatus.Active,
        department: findEntity(formValues.department, departmentOptions),
        role: findEntity(formValues.role, rolesOptions),
        seniority: findEntity(formValues.seniority, senioritiesOptions),
        membershipType: formValues.membershipType || null,
        country: formValues.countryName || null,
        city: formValues.cityName || null,
        managerId:
          formValues.managerId && selectedManagerName
            ? { id: formValues.managerId, name: selectedManagerName }
            : null,
        isManager: formValues.isManager || false,
      };
    };

    // Watch isPlaceholder field to conditionally show/hide fields
    // Declared before the conflict resolution hook so it can be passed to field configs
    const isPlaceholder = useWatch({ control: form.control, name: 'isPlaceholder' });

    // Use conflict resolution hook
    // Pass raw form values for conflict checking (accurate comparison)
    const {
      showConflictModal,
      conflictSnapshots,
      selectedDateRange,
      closeConflictModal,
      handleSubmitWithConflicts,
      handleResolveConflicts,
    } = useTeamMemberChangeConflictResolution<TeamMemberInfoFormData, TeamMemberInfoFieldKey>({
      tag: TAG,
      form,
      teamMemberId: teamMemberData?.id ? String(teamMemberData.id) : undefined,
      checkConflicts,
      isCheckingConflicts,
      onFormStateChange,
      getFormData: getFormDataAdapter,
      onDirectSave,
      fields: teamMemberInfoConflictFields(!!isPlaceholder),
      currentFormValues: getFormValuesForConflicts(),
    });

    /**
     * Submit handler with conflict detection
     */
    const handleSubmit = async (dateRange?: SubmitDateRange): Promise<void> => {
      // Skip conflict detection for Create mode
      if (mode === FeatureMode.Create) {
        await baseHandleSubmit();
        return;
      }

      // Use the conflict resolution hook for Edit/EditChange modes
      await handleSubmitWithConflicts(baseHandleSubmit, dateRange);
    };

    // Watch country field to enable/disable city field
    const watchedCountry = useWatch({ control: form.control, name: 'country' });
    // Also watch countryName — in edit mode the ID resolves asynchronously, but countryName is set immediately
    const watchedCountryName = useWatch({ control: form.control, name: 'countryName' });

    // Watch fields to set the warning message
    const watchedDepartment = useWatch({ control: form.control, name: 'department' });
    const watchedRole = useWatch({ control: form.control, name: 'role' });
    const watchedSeniority = useWatch({ control: form.control, name: 'seniority' });

    const isDeletedDepartmentSelected = departmentOptions.find(
      department => department.id?.toString() === watchedDepartment
    )?.isDeleted;
    const isDeletedRoleSelected = rolesOptions.find(
      role => role.id?.toString() === watchedRole
    )?.isDeleted;
    const isDeletedSenioritySelected = senioritiesOptions.find(
      seniority => seniority.id?.toString() === watchedSeniority
    )?.isDeleted;

    const departmentWarning = isDeletedDepartmentSelected
      ? t('details.deletedEntityWanring')
      : undefined;
    const roleWarning = isDeletedRoleSelected ? t('details.deletedEntityWanring') : undefined;
    const seniorityWarning = isDeletedSenioritySelected
      ? t('details.deletedEntityWanring')
      : undefined;

    useEffect(() => {
      const currentCountry = watchedCountry || '';
      const previousCountry = selectedCountry;

      if (currentCountry !== previousCountry) {
        setSelectedCountry(currentCountry);

        // Reset city when country changes, but NOT on form reset after save.
        // A form reset sets country → '' while countryName is still set (name comes from backend).
        const isFormReset = currentCountry === '' && !!watchedCountryName;

        if ((previousCountry !== '' || currentCountry === '') && !isFormReset) {
          form.setValue('city', '');
          form.setValue('cityName', '');
          if (!currentCountry) {
            setResolvedCountryCode('');
          }
        }
      }
    }, [watchedCountry, watchedCountryName, selectedCountry, form]);

    // Handle the case where country is cleared via X but the country code was never set
    // (initial load: countryName was set from backend but country code field stayed '').
    // In this case watchedCountry stays '' throughout, so the effect above never fires.
    // We detect the clear by watching countryName transition from non-empty to empty.
    useEffect(() => {
      if (watchedCountryName) {
        countryNameWasSetRef.current = true;
      } else if (countryNameWasSetRef.current && !watchedCountryName && !watchedCountry) {
        // countryName was previously set and is now cleared — reset city
        countryNameWasSetRef.current = false;
        form.setValue('city', '');
        form.setValue('cityName', '');
        setResolvedCountryCode('');
      }
    }, [watchedCountryName, watchedCountry, form]);

    // Handle validation when switching placeholder mode
    useEffect(() => {
      if (isPlaceholder) {
        // Clear validation errors for hidden fields, keep the values for when user switches back
        form.clearErrors(['email', 'phoneNumber', 'country', 'city', 'about']);
        // Re-run the resolver so formState.isValid recomputes against the placeholder-aware
        // schema rules — clearErrors alone does not flip isValid back to true, which would
        // leave the footer Save button disabled even though the visible fields are valid.
        form.trigger();
      } else {
        // When switching back from placeholder mode, only re-validate fields that were already touched
        // This prevents showing errors for fields the user hasn't interacted with yet
        const touchedFields = form.formState.touchedFields;
        const fieldsToValidate = (
          ['email', 'phoneNumber', 'country', 'city', 'about'] as const
        ).filter(field => touchedFields[field]);
        if (fieldsToValidate.length > 0) {
          form.trigger(fieldsToValidate as any);
        }
      }
    }, [isPlaceholder, form]);

    const shouldShowWarningForMemberId = (item: DropdownSearchItem) => {
      return item.isDeleted ? t('details.deletedEntityWanring') : undefined;
    };

    // Include deleted manager in dropdown options if exists
    const additionalManagerItems = useMemo(() => {
      if (teamMemberData?.manager?.isDeleted) {
        return [
          {
            id: teamMemberData.manager.id.toString(),
            name: teamMemberData.manager.name,
            secondaryText: teamMemberData.manager.email ?? undefined,
            avatarLink: teamMemberData.manager.profileImageUrl ?? undefined,
            isDeleted: true,
          },
        ];
      }
      return undefined;
    }, [teamMemberData?.manager]);

    // Create initial selected items for department, role, and seniority (similar to manager in project-info-tab)
    const initialDepartmentItem = useMemo(() => {
      if (teamMemberData?.department && typeof teamMemberData.department === 'object') {
        return {
          value: teamMemberData.department.id.toString(),
          label: teamMemberData.department.name,
          isDeleted: teamMemberData.department.isDeleted,
        };
      }
      return null;
    }, [teamMemberData?.department]);

    const initialRoleItem = useMemo(() => {
      if (teamMemberData?.role && typeof teamMemberData.role === 'object') {
        return {
          value: teamMemberData.role.id.toString(),
          label: teamMemberData.role.name,
          isDeleted: teamMemberData.role.isDeleted,
        };
      }
      return null;
    }, [teamMemberData?.role]);

    const initialSeniorityItem = useMemo(() => {
      if (teamMemberData?.seniority && typeof teamMemberData.seniority === 'object') {
        return {
          value: teamMemberData.seniority.id.toString(),
          label: teamMemberData.seniority.name,
          isDeleted: teamMemberData.seniority.isDeleted,
        };
      }
      return null;
    }, [teamMemberData?.seniority]);

    // Create initial selected item for manager
    const initialManagerItem = useMemo(() => {
      if (teamMemberData?.manager) {
        return {
          id: teamMemberData.manager.id.toString(),
          name: teamMemberData.manager.name,
          secondaryText: teamMemberData.manager.email ?? undefined,
          avatarLink: teamMemberData.manager.profileImageUrl ?? undefined,
          isDeleted: teamMemberData.manager.isDeleted,
        };
      }
      return undefined;
    }, [teamMemberData?.manager]);

    // Create initial selected items for country and city
    const initialCountryItem = useMemo(() => {
      if (teamMemberData?.country) {
        return {
          id: '',
          name: teamMemberData.country,
        };
      }
      return undefined;
    }, [teamMemberData?.country]);

    const initialCityItem = useMemo(() => {
      if (teamMemberData?.city) {
        return {
          id: '',
          name: teamMemberData.city,
        };
      }
      return undefined;
    }, [teamMemberData?.city]);

    // Separate callbacks for each dropdown to enable fetching independently
    const handleDepartmentsOpenChange = (open: boolean) => {
      if (open && !shouldFetchDepartments) {
        setShouldFetchDepartments(true);
      }
    };

    const handleRolesOpenChange = (open: boolean) => {
      if (open && !shouldFetchRoles) {
        setShouldFetchRoles(true);
      }
    };

    const handleSenioritiesOpenChange = (open: boolean) => {
      if (open && !shouldFetchSeniorities) {
        setShouldFetchSeniorities(true);
      }
    };

    // Called when city dropdown opens. If we have a country name but no resolved code yet
    // (country dropdown was never opened), fetch countries to resolve the code first.
    // Lazily resolves the country code from the country name.
    // Called when user opens country or city selector — never on mount.
    // Stores result in resolvedCountryCode state only (never in the form field).
    const resolveCountryCodeIfNeeded = (countryName: string | undefined) => {
      if (!countryName || resolvedCountryCode || isResolvingCountryCode) return;

      setIsResolvingCountryCode(true);

      getCountriesData()
        .then(countries => {
          const match = countries.find(c => c.name === countryName);
          if (match) {
            setResolvedCountryCode(match.id);
          }
        })
        .catch(error => {
          logger.error(TAG, 'Failed to resolve country code from name', { countryName, error });
        })
        .finally(() => {
          setIsResolvingCountryCode(false);
        });
    };

    const tabContent = (
      <Form {...form}>
        <form className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('details.tabs.info.mainInfoTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('details.tabs.info.fullName')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t('details.tabs.info.fullNamePlaceholder')}
                        disabled={isReadMode}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {(mode === FeatureMode.Create ||
                teamMemberData?.type === TeamMemberType.Placeholder) && (
                <>
                  <ToggleWithDescription
                    name="isPlaceholder"
                    control={form.control}
                    label={t('placeholder')}
                    description={t('details.tabs.info.placeholderDescription')}
                    disabled={isReadMode}
                  />
                  {mode !== FeatureMode.Create &&
                    teamMemberData?.type === TeamMemberType.Placeholder &&
                    !isPlaceholder && (
                      <Alert variant="warning">
                        <InfoIcon className="h-4 w-4" />
                        <AlertDescription>
                          {t('details.tabs.info.placeholderToMemberWarning')}
                        </AlertDescription>
                      </Alert>
                    )}
                </>
              )}

              {!isPlaceholder && (
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('details.tabs.info.email')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="email"
                          placeholder={t('details.tabs.info.emailPlaceholder')}
                          disabled={
                            isReadMode ||
                            (mode === FeatureMode.Edit &&
                              teamMemberData?.status &&
                              [
                                MemberStatus.Active,
                                MemberStatus.Invited,
                                MemberStatus.Archived,
                              ].includes(teamMemberData.status))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <DatePicker
                name="dateOfJoin"
                control={form.control}
                label={t('details.tabs.info.dateOfJoin')}
                inputDisabled={
                  isReadMode ||
                  ((mode === FeatureMode.Edit || mode === FeatureMode.EditChange) &&
                    !!teamMemberData?.dateOfJoin)
                }
                placeholder={isReadMode ? t('details.tabs.info.notSelected') : undefined}
              />

              {mode === FeatureMode.Create && (
                <FileUploadField
                  name="avatar"
                  control={form.control}
                  label={t('details.tabs.info.avatar')}
                  disabled={isReadMode}
                  helpText={t('common:fileUpload.helpText')}
                />
              )}

              <SingleSelectWithPagination
                name="managerId"
                initialSelectedItem={initialManagerItem}
                showWarning={shouldShowWarningForMemberId}
                additionalItems={additionalManagerItems}
                excludeIds={teamMemberData?.id ? [String(teamMemberData.id)] : undefined}
                control={form.control}
                label={t('details.tabs.info.manager')}
                placeholder={
                  isReadMode
                    ? t('details.tabs.info.notSelected')
                    : t('details.tabs.info.managerPlaceholder')
                }
                disabled={isReadMode}
                showAvatars={true}
                showDescriptionInOptions={true}
                showDescriptionInSelector={false}
                avatarShape="circle"
                queryKey={['team-members-short', 'managers']}
                loadItems={loadManagers}
                searchItems={searchManagers}
                onSelect={item => setSelectedManagerName(item?.name)}
                isClearSelectionEnabled={true}
              />

              {!isPlaceholder && (
                <PhoneNumberInput
                  name="phoneNumber"
                  control={form.control}
                  label={t('details.tabs.info.phoneNumber')}
                  placeholder={t('details.tabs.info.phoneNumberPlaceholder')}
                  disabled={isReadMode}
                />
              )}

              {!isPlaceholder && (
                <div className="flex gap-4">
                  <div className="flex-1">
                    <SingleSelectWithPagination
                      name="country"
                      displayName="countryName"
                      initialSelectedItem={initialCountryItem}
                      control={form.control}
                      setValue={form.setValue}
                      label={t('details.tabs.info.location')}
                      disabled={isReadMode}
                      queryKey={['countries']}
                      loadItems={loadCountries}
                      searchItems={searchCountries}
                      onOpenChange={open => {
                        if (open) resolveCountryCodeIfNeeded(watchedCountryName);
                      }}
                      onSelect={item => {
                        // Only update when a real country code is available (not the initialSelectedItem
                        // placeholder which has id: '' until the dropdown data loads)
                        if (item?.id) {
                          setResolvedCountryCode(item.id);
                        } else if (!item) {
                          setResolvedCountryCode('');
                        }
                      }}
                      isClearSelectionEnabled={true}
                    />
                  </div>
                  <div className="flex-1">
                    <SingleSelectWithPagination
                      name="city"
                      displayName="cityName"
                      initialSelectedItem={initialCityItem}
                      control={form.control}
                      setValue={form.setValue}
                      label={t('details.tabs.info.city')}
                      disabled={isReadMode || (!resolvedCountryCode && !watchedCountryName)}
                      loading={isResolvingCountryCode}
                      queryKey={['cities', resolvedCountryCode]}
                      loadItems={cursor => loadCities(resolvedCountryCode, cursor)}
                      isClearSelectionEnabled={true}
                      searchItems={(searchTerm, cursor) =>
                        searchCities(resolvedCountryCode, searchTerm, cursor)
                      }
                      showDescriptionInOptions={true}
                      onOpenChange={open => {
                        if (open) resolveCountryCodeIfNeeded(watchedCountryName);
                      }}
                    />
                  </div>
                </div>
              )}
              {/*TODO uncomment once tags will be implemented*/}
              {/*<TagsSelect
                name="tags"
                control={form.control}
                label={t('details.tabs.info.tags')}
                placeholder={
                  isReadMode
                    ? t('details.tabs.info.notSelected')
                    : t('details.tabs.info.tagsPlaceholder')
                }
                disabled={isReadMode}
              />*/}

              {!isPlaceholder && (
                <FormField
                  control={form.control}
                  name="about"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('details.tabs.info.about')}</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder={t('details.tabs.info.aboutPlaceholder')}
                          rows={4}
                          disabled={isReadMode}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="mr-4">
                {t('details.tabs.info.organizationalDetailsTitle')}
              </CardTitle>
              {!isReadMode && hasPermission(OpsiumPolicy.OrgStructureViewAndEdit) && (
                <ResponsiveActionLinks
                  actions={[
                    {
                      label: t('details.tabs.info.manageDepartments'),
                      onClick: () => {
                        navigate(SettingsRoutes.Departments);
                      },
                    },
                    {
                      label: t('details.tabs.info.manageRoles'),
                      onClick: () => {
                        navigate(SettingsRoutes.Roles);
                      },
                    },
                    {
                      label: t('details.tabs.info.manageSeniorityLevels'),
                      onClick: () => {
                        navigate(SettingsRoutes.Seniorities);
                      },
                    },
                  ]}
                />
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <SingleSelect
                name="department"
                warning={departmentWarning}
                control={form.control}
                options={departmentOptions.map(dept => ({
                  value: dept.id.toString(),
                  label: dept.name,
                  isDeleted: dept.isDeleted,
                }))}
                label={t('details.tabs.info.department')}
                placeholder={
                  isReadMode
                    ? t('details.tabs.info.notSelected')
                    : t('details.tabs.info.departmentPlaceholder')
                }
                emptyMessage={
                  isLoadingDepartments
                    ? t('common:loading')
                    : t('details.tabs.info.noDepartmentsFound')
                }
                disabled={isReadMode}
                loading={isLoadingDepartments}
                searchable={true}
                initialSelectedItem={initialDepartmentItem}
                onOpenChange={handleDepartmentsOpenChange}
                isClearSelectionEnabled={true}
              />

              <SingleSelect
                name="role"
                warning={roleWarning}
                control={form.control}
                options={rolesOptions.map(role => ({
                  value: role.id.toString(),
                  label: role.name,
                  isDeleted: role.isDeleted,
                }))}
                label={t('details.tabs.info.role')}
                placeholder={
                  isReadMode
                    ? t('details.tabs.info.notSelected')
                    : t('details.tabs.info.rolePlaceholder')
                }
                emptyMessage={
                  isLoadingRoles ? t('common:loading') : t('details.tabs.info.noRolesFound')
                }
                disabled={isReadMode}
                loading={isLoadingRoles}
                searchable={true}
                initialSelectedItem={initialRoleItem}
                onOpenChange={handleRolesOpenChange}
                isClearSelectionEnabled={true}
              />

              <SingleSelect
                name="seniority"
                warning={seniorityWarning}
                control={form.control}
                options={senioritiesOptions.map(level => ({
                  value: level.id.toString(),
                  label: level.name,
                  isDeleted: level.isDeleted,
                }))}
                label={t('details.tabs.info.seniority')}
                placeholder={
                  isReadMode
                    ? t('details.tabs.info.notSelected')
                    : t('details.tabs.info.seniorityPlaceholder')
                }
                emptyMessage={
                  isLoadingSeniorities
                    ? t('common:loading')
                    : t('details.tabs.info.noSeniorityLevelsFound')
                }
                disabled={isReadMode}
                loading={isLoadingSeniorities}
                searchable={true}
                initialSelectedItem={initialSeniorityItem}
                onOpenChange={handleSenioritiesOpenChange}
                isClearSelectionEnabled={true}
              />

              <MembershipTypeSelect
                name="membershipType"
                control={form.control}
                label={t('details.tabs.info.membershipType.label')}
                placeholder={isReadMode ? t('details.tabs.info.notSelected') : undefined}
                disabled={isReadMode}
              />

              <ToggleWithDescription
                name="isManager"
                control={form.control}
                label={t('details.tabs.info.manager')}
                description={t('details.tabs.info.managerDescription')}
                disabled={isReadMode}
              />
            </CardContent>
          </Card>

          {/* Custom attributes — rendered as the last section of the Info tab.
              Hidden entirely when no (supported) attributes are configured. */}
          {customAttributes && customAttributes.length > 0 && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <CardTitle className="mr-4">{t('details.tabs.customAttributes.title')}</CardTitle>
                {/* TODO(OPS-421): uncomment to enable navigation to the custom attributes settings page
                {!isReadMode && hasPermission(OpsiumPolicy.OrgStructureViewAndEdit) && (
                  <ResponsiveActionLinks
                    actions={[
                      {
                        label: t('details.tabs.customAttributes.manageCustomAttributes'),
                        onClick: () => {
                          navigate(SettingsRoutes.CustomAttributes);
                        },
                      },
                    ]}
                  />
                )} */}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <CustomAttributesFields
                    customAttributes={customAttributes.map(attr => ({
                      id: attr.id,
                      name: attr.name,
                      type: attr.type,
                      options: attr.options,
                    }))}
                    form={form as ReturnType<typeof useForm<any>>}
                    fieldPrefix="customAttributes"
                    isReadMode={isReadMode}
                    t={(key: string, options?: any) =>
                      t(`details.tabs.customAttributes.${key}`, options) as string
                    }
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </form>
      </Form>
    );

    return (
      <>
        <BaseFeatureTab
          {...props}
          ref={ref}
          isArchivedFn={member => member?.status === MemberStatus.Archived}
          onSubmit={handleSubmit}
          isValid={form.formState.isValid}
          getFormData={getFormDataAdapter}
          isCheckingConflicts={isCheckingConflicts}
        >
          {tabContent}
        </BaseFeatureTab>

        {/* Conflict Resolution Modal */}
        {showConflictModal && selectedDateRange && (
          <ResolveConflictsModal
            isOpen={showConflictModal}
            onClose={closeConflictModal}
            snapshots={conflictSnapshots}
            currentFormValues={getFormValuesForConflicts()}
            selectedDateRange={selectedDateRange}
            fields={teamMemberInfoConflictFields(!!isPlaceholder)}
            onResolve={handleResolveConflicts}
            t={t}
            translationNamespace="authenticated.team"
          />
        )}
      </>
    );
  }
);
